/**
 * Период для «Операций» и «Аналитики» — один и тот же выбор.
 *
 * По умолчанию стоит период от зарплаты до зарплаты: так видно, на что
 * уходит один доход. Если главный доход не определить (два близких дохода,
 * доход каждую неделю или «когда как»), по умолчанию — этот месяц. Человек
 * может сменить период в любой момент, и выбор переживает переход между
 * экранами, пока открыто приложение.
 *
 * Будущих периодов («Завтра», «Следующий месяц») нет: будущих операций в
 * приложении нет, и списки были бы пустыми.
 */
import { useEffect, useRef, useState } from 'react';
import type { PlainDate, ReportPeriod } from '@skoka/core';
import { SheetWrap } from './sheet.js';
import { Icon } from './icon.js';
import { Tip } from './tip.js';
import { dayMonth } from './text.js';
import { DateButton } from './date-picker.js';

export type RangeKind =
  | 'salary' | 'report'
  | 'today' | 'yesterday' | 'week' | 'prev_week' | 'month' | 'prev_month'
  | 'year' | 'all' | 'custom';

export interface RangeChoice {
  kind: RangeKind;
  /** Ключ прошлого периода от зарплаты до зарплаты. */
  key?: string;
  year?: number;
  from?: PlainDate;
  to?: PlainDate;
}

export interface RangeCtx {
  today: PlainDate;
  /** Периоды от зарплаты до зарплаты по возрастанию; последний — идущий. */
  periods: readonly ReportPeriod[];
  /**
   * Главного дохода нет (зарплата 7-го и подработка 5-го), а зарплата есть:
   * её периоды для выбора «С зарплаты» — по самой большой зарплате.
   */
  payday?: { periods: readonly ReportPeriod[]; name: string; others: readonly string[] };
}

export interface Range {
  from: PlainDate | null;
  to: PlainDate | null;
  /** Если выбран период от зарплаты до зарплаты — он сам. */
  period: ReportPeriod | null;
  /** Календарный месяц, в котором сменился доход: оценки по плану месяца нет. */
  changed?: boolean;
}

const TITLE: Record<Exclude<RangeKind, 'salary' | 'report' | 'year' | 'custom'>, string> = {
  today: 'Сегодня', yesterday: 'Вчера', week: 'Эта неделя', prev_week: 'Прошлая неделя',
  month: 'Этот месяц', prev_month: 'Прошлый месяц', all: 'Всё время',
};

const iso = (d: Date): PlainDate => d.toISOString().slice(0, 10) as PlainDate;
const utc = (p: PlainDate): Date => new Date(`${p}T00:00:00Z`);
const shift = (p: PlainDate, days: number): PlainDate => {
  const d = utc(p); d.setUTCDate(d.getUTCDate() + days); return iso(d);
};
const SHORT = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
/** «5 окт» — для вкладки, где места мало. */
export const shortDay = (d: PlainDate): string => `${Number(d.slice(8, 10))} ${SHORT[Number(d.slice(5, 7)) - 1]}`;

/**
 * Идущий период — от зарплаты или выплаты себе, а не календарный месяц.
 *
 * Месяц, который закончит смена дохода (с 1 октября до зарплаты 20-го), —
 * тоже свой период: он не совпадает с календарным, и «Этот месяц» его не
 * заменяет.
 */
export const salaryPeriod = (ctx: RangeCtx): ReportPeriod | null => {
  const p = ctx.periods[ctx.periods.length - 1];
  return p && (p.kind !== 'month' || !!p.next) ? p : null;
};

/**
 * Период «С зарплаты»: главного дохода, а без него — самой большой зарплаты.
 * Только для выбора: по умолчанию без главного дохода стоит этот месяц.
 */
export const paydayPeriod = (ctx: RangeCtx): ReportPeriod | null => {
  const own = salaryPeriod(ctx);
  if (own) return own;
  const list = ctx.payday?.periods ?? [];
  const p = list[list.length - 1];
  return p && p.to === null ? p : null;
};

/** Все периоды для выбора по ключу: главные и самой большой зарплаты. */
const knownPeriods = (ctx: RangeCtx): readonly ReportPeriod[] => [...ctx.periods, ...(ctx.payday?.periods ?? [])];

/** Календарный месяц как период — с ключом месяца, чтобы читалась его основа. */
const calendarMonth = (own: ReportPeriod, from: PlainDate, to: PlainDate, today: PlainDate): ReportPeriod => ({
  key: own.key, kind: 'month', from, start: 'calendar', certain: true,
  to: to < today ? to : null, expectedTo: to, plannedTo: to,
  observedFrom: own.observedFrom, partial: own.partial, overdue: false, why: 'Календарный месяц',
});

/** Закрытый период, который не совпадает с календарным месяцем, — его выбирают из листа. */
const notWholeMonth = (p: ReportPeriod): boolean => p.kind !== 'month' || !!p.endedBySwitch
  || p.from.slice(8, 10) !== '01' || (!!p.to && !!p.plannedTo && p.to !== p.plannedTo);

/** Что стоит по умолчанию: период от зарплаты, а без него — этот месяц. */
export const defaultRange = (ctx: RangeCtx): RangeChoice =>
  (salaryPeriod(ctx) ? { kind: 'salary' } : { kind: 'month' });

/** Границы выбора включительно; `null` — без границы. */
export function rangeOf(c: RangeChoice, ctx: RangeCtx): Range {
  const { today } = ctx;
  const d = utc(today);
  const none = { period: null };
  switch (c.kind) {
    case 'salary': {
      const p = paydayPeriod(ctx);
      return p ? { from: p.from, to: p.to ?? today, period: p } : rangeOf({ kind: 'month' }, ctx);
    }
    case 'report': {
      const p = knownPeriods(ctx).find((x) => x.key === c.key);
      return p ? { from: p.from, to: p.to ?? today, period: p } : rangeOf(defaultRange(ctx), ctx);
    }
    case 'today': return { from: today, to: today, ...none };
    case 'yesterday': { const y = shift(today, -1); return { from: y, to: y, ...none }; }
    case 'week': case 'prev_week': {
      // Неделя с понедельника, как в российском календаре.
      const back = (d.getUTCDay() + 6) % 7 + (c.kind === 'prev_week' ? 7 : 0);
      const from = shift(today, -back);
      return { from, to: shift(from, 6), ...none };
    }
    case 'month': case 'prev_month': {
      const k = c.kind === 'prev_month' ? -1 : 0;
      const from = iso(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + k, 1)));
      const to = iso(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + k + 1, 0)));
      // Без главного дохода период и есть календарный месяц — у него свой план.
      // «Этот месяц» — весь календарный месяц во всех блоках: период,
      // оборванный сменой дохода (1–19 октября), — отдельный, а здесь у месяца
      // его календарные границы.
      const own = ctx.periods.find((p) => p.kind === 'month' && p.from === from) ?? null;
      const whole = !!own && (own.to === to || (own.to === null && !own.next));
      const period = !own ? null : whole ? own : calendarMonth(own, from, to, today);
      return { from, to: period && !period.to ? today : to, period, ...(own && !whole ? { changed: true } : {}) };
    }
    case 'year': {
      const y = c.year ?? d.getUTCFullYear();
      return { from: `${y}-01-01` as PlainDate, to: `${y}-12-31` as PlainDate, ...none };
    }
    case 'all': return { from: null, to: null, ...none };
    case 'custom': return { from: c.from ?? null, to: c.to ?? null, ...none };
  }
}

/** «1–30 сентября», «5 октября – 4 ноября», «Всё время». */
export function rangeText(c: RangeChoice, ctx: RangeCtx): string {
  const { from, to } = rangeOf(c, ctx);
  if (!from && !to) return 'Всё время';
  if (from && to && from === to) return dayMonth(from, ctx.today);
  if (from && to && from.slice(0, 7) === to.slice(0, 7)) {
    return `${Number(from.slice(8, 10))}–${dayMonth(to, ctx.today)}`;
  }
  return `${from ? dayMonth(from, ctx.today) : '…'} – ${to ? dayMonth(to, ctx.today) : '…'}`;
}

const startWord = (p: ReportPeriod): string => (p.kind === 'stable' ? 'С выплаты' : p.kind === 'month' ? 'С' : 'С зарплаты');

/** Название выбора для вкладки и подписей. */
export function rangeLabel(c: RangeChoice, ctx: RangeCtx): string {
  if (c.kind === 'salary') {
    const p = paydayPeriod(ctx);
    return p ? `${startWord(p)} ${shortDay(p.from)}` : TITLE.month;
  }
  if (c.kind === 'report') {
    const p = knownPeriods(ctx).find((x) => x.key === c.key);
    return p ? `${shortDay(p.from)} – ${shortDay(p.to ?? ctx.today)}` : TITLE.month;
  }
  if (c.kind === 'year') return String(c.year ?? Number(ctx.today.slice(0, 4)));
  if (c.kind === 'custom') return rangeText(c, ctx);
  return TITLE[c.kind];
}

export const sameChoice = (a: RangeChoice, b: RangeChoice): boolean =>
  a.kind === b.kind && a.key === b.key && a.year === b.year && a.from === b.from && a.to === b.to;

/** Вкладки — все готовые периоды листа, в том же порядке. Свой период — в листе. */
export function quickRanges(ctx: RangeCtx, years: readonly number[] = [Number(ctx.today.slice(0, 4))]): RangeChoice[] {
  return [
    ...(paydayPeriod(ctx) ? [{ kind: 'salary' as const }] : []),
    ...(['today', 'yesterday', 'week', 'prev_week', 'month', 'prev_month'] as const).map((kind) => ({ kind })),
    ...years.map((year) => ({ kind: 'year' as const, year })),
    { kind: 'all' },
  ];
}

/** Конец периода для подписи «7 окт – 6 ноя»: закрытый — по факту, идущий — ожидаемый. */
const periodSpan = (p: ReportPeriod, today: PlainDate): string => {
  const end = p.to ?? p.expectedTo ?? p.plannedTo ?? null;
  return end ? `${shortDay(p.from)} – ${shortDay(end)}` : `с ${shortDay(p.from)}`;
};

/** Пояснение к «С зарплаты», когда главного дохода нет: по какому доходу и почему. */
export function PaydayTip({ ctx }: { ctx: RangeCtx }) {
  if (salaryPeriod(ctx) || !ctx.payday) return null;
  const { name, others } = ctx.payday;
  return (
    <Tip label="Почему от этой зарплаты">
      Период — от прихода «{name}» до следующего: это самый большой регулярный доход.
      {others.length > 0 && <> {others.map((o) => `«${o}»`).join(', ')} тоже {others.length > 1 ? 'входят' : 'входит'} в период,
        но {others.length > 1 ? 'его границ не задают' : 'его границ не задаёт'}.</>}
    </Tip>
  );
}

/**
 * Выбор живёт, пока открыто приложение, и общий для двух экранов.
 * `null` — человек ничего не выбирал: действует значение по умолчанию.
 */
export const sharedFilter: {
  range: RangeChoice | null; accountId: string | null;
  /** Поиск, с которым открыть «Операции» один раз: «Посмотреть операции» из разбора. */
  query: string | null;
} = {
  range: null, accountId: null, query: null,
};

/** Выбранный период или значение по умолчанию. */
export const currentChoice = (ctx: RangeCtx): RangeChoice => sharedFilter.range ?? defaultRange(ctx);

/** Отметка выбора — круг, у выбранного залитый с галочкой. */
export const Radio = ({ on }: { on: boolean }) => (
  <span className={`ops-radio${on ? ' on' : ''}`} aria-hidden="true">{on && <Icon name="check" size={15} />}</span>
);

/** Строка «Период ›» и вкладки всех готовых периодов. */
export function PeriodTabs({ choice, ctx, years, onPick, onOpen }: {
  choice: RangeChoice; ctx: RangeCtx; years?: readonly number[]; onPick: (c: RangeChoice) => void; onOpen: () => void;
}) {
  const quick = quickRanges(ctx, years);
  // Выбранный в листе (свой или прошлый период) — первым: его нет среди готовых.
  const tabs = quick.some((q) => sameChoice(q, choice)) ? quick : [choice, ...quick];
  const strip = useRef<HTMLDivElement>(null);
  const key = `${choice.kind}:${choice.key ?? ''}:${choice.year ?? ''}:${choice.from ?? ''}:${choice.to ?? ''}`;
  // Выбранная вкладка всегда видна: выбор «Вчера» в листе или «Всё время»
  // в конце ленты прокручивает ленту к ней. Вкладка встаёт в начало ленты:
  // по центру слева торчал обрезок соседней («я неделя»).
  useEffect(() => {
    const box = strip.current;
    const on = box?.querySelector<HTMLElement>('.ops-tab.on');
    if (!box || !on) return;
    // Сдвиг — от самой ленты, а не от страницы: `offsetLeft` считал бы и
    // кнопку «Период» слева.
    const left = box.scrollLeft + on.getBoundingClientRect().left - box.getBoundingClientRect().left - 6;
    box.scrollTo({ left: Math.max(0, left), behavior: 'auto' });
  }, [key]);
  return (
    // «Период» стоит на месте, листаются только вкладки: иначе при выборе
    // крайней вкладки кнопка уезжала за край экрана.
    <div className="ops-periods">
      <button className="ops-period" onClick={onOpen}>
        Период <Icon name="chevron-right" size={16} />
      </button>
      <div className="ops-tabs" role="tablist" aria-label="Период" ref={strip}>
        {tabs.map((t) => {
          const on = sameChoice(t, choice);
          return (
            <button key={`${t.kind}:${t.key ?? ''}:${t.year ?? ''}:${t.from ?? ''}:${t.to ?? ''}`} role="tab" aria-selected={on}
              className={`ops-tab${on ? ' on' : ''}`} onClick={() => onPick(t)}>{rangeLabel(t, ctx)}</button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Подсказка, почему период такой. «Понятно» убирает её насовсем — на всех
 * устройствах: это ответ человека, а не настройка экрана.
 */
export function PeriodHint({ ctx, extra, onDismiss }: {
  ctx: RangeCtx; extra?: string; onDismiss: () => void;
}) {
  const p = salaryPeriod(ctx);
  if (!p) return null;
  const what = p.kind === 'stable' ? 'с последней выплаты себе'
    : p.kind === 'month' ? 'идущий период: доход сменился, и он закончится раньше месяца'
      : p.start === 'schedule' ? 'с зарплаты по расписанию: она пришла до начала учёта'
        : 'с последней зарплаты';
  return (
    <div className="hint">
      <Icon name="circle-info" size={16} className="i" />
      <div>
        <p>Показываю <b>с {dayMonth(p.from, ctx.today)} — {what}</b>.{p.kind === 'month' ? '' : ' Так видно, на что уходит один доход.'}
          {' '}Период можно сменить в любой момент.{extra ? ` ${extra}` : ''}</p>
        <button className="ok" onClick={onDismiss}>Понятно</button>
      </div>
    </div>
  );
}

/** Лист выбора периода. */
export function PeriodSheet({ value, ctx, years, context, onClose, onApply }: {
  value: RangeChoice; ctx: RangeCtx; years: number[]; context: string;
  onClose: () => void; onApply: (c: RangeChoice) => void;
}) {
  const [picked, setPicked] = useState<RangeChoice>(value);
  const current = rangeOf(value, ctx);
  const [from, setFrom] = useState<string>(value.kind === 'custom' ? value.from ?? '' : current.from ?? '');
  const [to, setTo] = useState<string>(value.kind === 'custom' ? value.to ?? '' : current.to ?? ctx.today);
  const customOk = picked.kind !== 'custom' || (from !== '' && to !== '' && from <= to);
  const salary = paydayPeriod(ctx);
  const own = !!salaryPeriod(ctx);
  // Прошлые периоды — два последних закрытых, кроме целых календарных
  // месяцев (их выбирают «Этот / Прошлый месяц»). История видна и тогда,
  // когда идущий период — уже календарный месяц. Без главного дохода —
  // прошлые периоды самой большой зарплаты.
  const history = own || !ctx.payday ? ctx.periods : ctx.payday.periods;
  const past = history.filter((p) => p.to !== null && notWholeMonth(p)).slice(-2).reverse();

  const option = (c: RangeChoice, title: string) => (
    <button key={`${c.kind}:${c.key ?? ''}:${c.year ?? ''}`} className={`ops-option${sameChoice(picked, c) ? ' on' : ''}`}
      aria-pressed={sameChoice(picked, c)} onClick={() => setPicked(c)}>
      <span>{title}</span><Radio on={sameChoice(picked, c)} />
    </button>
  );

  return (
    <SheetWrap title="Период" onClose={onClose} className="ops-sheet">
      <p className="ops-context">{context}</p>
      <div className="ops-options">
        {(salary || past.length > 0) && (
          <>
            <div className="ops-group">{salary?.kind === 'stable' ? 'От выплаты до выплаты'
              : salary?.kind === 'salary' ? 'От зарплаты до зарплаты' : salary ? 'Периоды' : 'Прошлые периоды'}
              {' '}<PaydayTip ctx={ctx} /></div>
            {salary && option({ kind: 'salary' }, salary.kind === 'month'
              ? `Этот период · ${rangeLabel({ kind: 'salary' }, ctx).toLowerCase()}`
              : periodSpan(salary, ctx.today))}
            {past.map((p) => option({ kind: 'report', key: p.key }, periodSpan(p, ctx.today)))}
          </>
        )}
        <div className="ops-group">Календарные периоды</div>
        {(['today', 'yesterday', 'week', 'prev_week', 'month', 'prev_month'] as const)
          .map((k) => option({ kind: k }, TITLE[k]))}
        {years.length > 0 && <div className="ops-group">Годы</div>}
        {years.map((y) => option({ kind: 'year', year: y }, String(y)))}
        {option({ kind: 'all' }, TITLE.all)}
      </div>
      <div className={`ops-custom${picked.kind === 'custom' ? ' on' : ''}`}>
        <button className="ops-custom-head" aria-pressed={picked.kind === 'custom'}
          onClick={() => setPicked({ kind: 'custom' })}>
          <span>Свой период</span><Radio on={picked.kind === 'custom'} />
        </button>
        <div className="ops-dates">
          <DateField label="С" value={from} max={to || undefined} today={ctx.today}
            onChange={(v) => { setFrom(v); setPicked({ kind: 'custom' }); }} />
          <DateField label="По" value={to} min={from || undefined} today={ctx.today}
            onChange={(v) => { setTo(v); setPicked({ kind: 'custom' }); }} />
        </div>
        {picked.kind === 'custom' && !customOk && from && to && (
          <p className="ops-explain">Начало периода позже конца.</p>
        )}
      </div>
      <div className="impbar">
        <button className="btn pri" disabled={!customOk}
          onClick={() => onApply(picked.kind === 'custom'
            ? { kind: 'custom', from: from as PlainDate, to: to as PlainDate }
            : picked)}>
          Применить период
        </button>
      </div>
    </SheetWrap>
  );
}

/**
 * Поле даты своего периода.
 *
 * Видна наша подпись «1 окт 2026», а тап открывает свой календарь листом.
 */
function DateField({ label, value, min, max, today, onChange }: {
  label: string; value: string; min?: string | undefined; max?: string | undefined; today: PlainDate;
  onChange: (v: string) => void;
}) {
  const text = value ? `${shortDay(value as PlainDate)} ${value.slice(0, 4)}` : 'Выбрать';
  return (
    <div className="ops-date">
      <span className="k">{label}</span>
      <span className={`v${value ? '' : ' empty'}`}>{text}<Icon name="calendar" size={16} /></span>
      <DateButton overlay label={`${label}: ${value ? dayMonth(value as PlainDate, today) : 'не выбрано'}`}
        value={value} min={min} max={max} onChange={onChange} />
    </div>
  );
}

/** Годы, за которые есть операции, — для листа периода. */
export const yearsOf = (dates: readonly string[], today: PlainDate): number[] => {
  const ys = new Set(dates.map((x) => Number(x.slice(0, 4))));
  ys.add(Number(today.slice(0, 4)));
  return [...ys].filter((y) => Number.isFinite(y)).sort((a, b) => b - a);
};
